/*
 * ЧЕРТЁЖ В ТРЁХ ПРОЕКЦИЯХ — как в настоящей конструкторской документации.
 *
 * ЗАЧЕМ ОН НУЖЕН. Умение читать плоский чертёж и собирать по нему объёмную
 * деталь — самая недооценённая часть 3D-моделирования. Ребёнок, который видит
 * готовую 3D-модель, просто её повторяет; ребёнок, у которого есть только три
 * вида, вынужден представить деталь в голове. Это и есть навык.
 *
 * ПОЧЕМУ ЧЕРТЁЖ РИСУЕТСЯ ИЗ ДАННЫХ, А НЕ ИЗ МОДЕЛИ. Строить проекции
 * настоящей 3D-детали (HLR — удаление невидимых линий) ядро умеет, но это
 * медленно и даёт «сырой» результат без размерных линий и осей. Учебный
 * чертёж — это ИЛЛЮСТРАЦИЯ задания, у неё другая задача: она должна быть
 * читаемой и содержать ровно те размеры, которые нужно выдержать. Поэтому
 * урок описывает виды простыми примитивами, а мы аккуратно их рисуем.
 *
 * 🔴 ЕДИНЫЙ МАСШТАБ — ГЛАВНОЕ ЗДЕСЬ.
 *
 * Раньше толщины линий и кегль шрифта задавались в единицах детали (мм), а
 * каждый чертёж масштабировался по своим габаритам. Деталь 40 мм растягивалась
 * на ту же ширину, что и деталь 150 мм, — и линия «1.1 мм» на мелкой детали
 * выходила втрое жирнее, чем на крупной. Чертежи выглядели грубо и разнобойно.
 *
 * Теперь наоборот: чертёж целиком приводится к постоянной ширине холста
 * (CANVAS), а координаты детали умножаются на масштаб. Толщины и кегль заданы
 * в единицах холста и остаются одинаковыми на всех чертежах курса — как на
 * настоящем листе, где толщина линии не зависит от размера детали.
 *
 * РАСПОЛОЖЕНИЕ ВИДОВ — по первому углу (европейский стандарт, ГОСТ):
 *   слева сверху  — вид спереди (главный)
 *   справа от него — вид слева
 *   снизу под ним  — вид сверху
 */

import React from 'react';
import cl from './Blueprint.module.css';

/** Ширина холста в его собственных единицах. Все толщины — доли от неё. */
const CANVAS = 400;
/** Поле вокруг чертежа. */
const PAD = 16;
/** Промежуток между видами. */
const GAP = 30;
/** Вынос размерной линии за контур детали, в единицах холста. */
const DIM_OUT = 11;
/** Длина засечки на конце размерной линии. */
const TICK = 2.2;
/** Насколько выносная линия перескакивает размерную (по ГОСТ — 1…5 мм). */
const EXT_OVER = 2;

/**
 * Одна размерная линия с засечками и подписью.
 *
 * orient: 'h' — горизонтальная (ширина), 'v' — вертикальная (высота).
 *
 * По ГОСТ: выносные линии идут ОТ КОНТУРА детали и заходят за размерную
 * линию на 1…5 мм; на концах размерной — засечки под 45°; число ставится над
 * размерной линией (для вертикальной — слева от неё, повёрнутое на 90°).
 *
 * Выносные линии обязательны и должны быть ХОРОШО ВИДНЫ: без них непонятно,
 * между какими именно точками детали стоит размер. Именно это и было главной
 * претензией к прошлой версии.
 */
function Dimension({ x1, y1, x2, y2, label, orient, from }) {
    const isH = orient === 'h';
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;

    // Засечка под 45°, поперёк размерной линии.
    const tick = (x, y) => ({
        x1: x - TICK, y1: y + TICK, x2: x + TICK, y2: y - TICK,
    });

    return (
        <g className={cl.dim}>
            {/*
              * Выносные линии: от контура детали (from) до размерной линии,
              * с заходом за неё. Направление захода зависит от того, с какой
              * стороны детали стоит размер.
              */}
            {from !== undefined && (isH ? (
                <>
                    <line className={cl.extLine} x1={x1} y1={from}
                        x2={x1} y2={y1 + Math.sign(y1 - from) * EXT_OVER}/>
                    <line className={cl.extLine} x1={x2} y1={from}
                        x2={x2} y2={y2 + Math.sign(y2 - from) * EXT_OVER}/>
                </>
            ) : (
                <>
                    <line className={cl.extLine} x1={from} y1={y1}
                        x2={x1 + Math.sign(x1 - from) * EXT_OVER} y2={y1}/>
                    <line className={cl.extLine} x1={from} y1={y2}
                        x2={x2 + Math.sign(x2 - from) * EXT_OVER} y2={y2}/>
                </>
            ))}

            <line className={cl.dimLine} x1={x1} y1={y1} x2={x2} y2={y2}/>
            <line className={cl.dimLine} {...tick(x1, y1)}/>
            <line className={cl.dimLine} {...tick(x2, y2)}/>

            {/*
              * Число — над размерной линией, не касаясь её. Для вертикального
              * размера текст поворачивают на 90° против часовой: так его
              * читают, наклонив голову влево, как и требует ГОСТ.
              */}
            {/*
              * Подложка под числом РАЗРЫВАЕТ размерную линию — так число
              * читается, а не оказывается перечёркнутым. На бумажном чертеже
              * линию в этом месте просто не проводят.
              */}
            <text
                x={isH ? mx : mx}
                y={isH ? my - 4.5 : my}
                className={`${cl.dimText} ${cl.dimTextBg}`}
                textAnchor="middle"
                dominantBaseline={isH ? 'auto' : 'central'}
                transform={isH ? undefined : `rotate(-90 ${mx} ${my})`}
            >{label}</text>
            <text
                x={isH ? mx : mx}
                y={isH ? my - 4.5 : my}
                className={cl.dimText}
                textAnchor="middle"
                dominantBaseline={isH ? 'auto' : 'central'}
                transform={isH ? undefined : `rotate(-90 ${mx} ${my})`}
            >{label}</text>
        </g>
    );
}


/**
 * Один вид детали.
 *
 * shapes — примитивы вида:
 *   { type:'rect', x, y, w, h, hidden? }
 *   { type:'circle', cx, cy, r, hidden? }
 *   { type:'line', x1, y1, x2, y2, hidden? }
 *   { type:'label', x, y, text }  — подпись детали (когда их на виде две)
 * hidden:true рисуется штриховой линией — это то, что скрыто внутри детали.
 *
 * Координаты приходят в миллиметрах детали и умножаются на общий масштаб k:
 * так толщины линий, заданные в единицах холста, остаются одинаковыми на
 * любом чертеже.
 */
function View({ title, width, height, shapes = [], dims = [], offsetX, offsetY, k }) {
    const S = (v) => v * k;

    return (
        <g transform={`translate(${offsetX} ${offsetY})`}>
            {/*
              * Название проекции — левым краем над видом, отдельной строкой
              * выше подписей деталей. По центру оно наезжало на подпись
              * детали, стоящую там же.
              */}
            <text x={0} y={-DIM_OUT - 20} className={cl.viewTitle}>{title}</text>

            {shapes.map((s, i) => {
                const cls = s.hidden ? cl.hiddenLine : cl.solidLine;
                if (s.type === 'rect') {
                    return <rect key={i} className={cls}
                        x={S(s.x)} y={S(s.y)} width={S(s.w)} height={S(s.h)}
                        rx={s.r ? S(s.r) : 0}/>;
                }
                if (s.type === 'circle') {
                    return (
                        <g key={i}>
                            <circle className={cls}
                                cx={S(s.cx)} cy={S(s.cy)} r={S(s.r)}/>
                            {/* Осевые линии отверстия — штрихпунктир, как в ГОСТ */}
                            <line className={cl.axisLine}
                                x1={S(s.cx - s.r) - 4} y1={S(s.cy)}
                                x2={S(s.cx + s.r) + 4} y2={S(s.cy)}/>
                            <line className={cl.axisLine}
                                x1={S(s.cx)} y1={S(s.cy - s.r) - 4}
                                x2={S(s.cx)} y2={S(s.cy + s.r) + 4}/>
                        </g>
                    );
                }
                if (s.type === 'line') {
                    return <line key={i} className={cls}
                        x1={S(s.x1)} y1={S(s.y1)} x2={S(s.x2)} y2={S(s.y2)}/>;
                }
                /*
                 * Подпись детали. Нужна там, где на одном виде показаны ДВЕ
                 * детали сразу — коробка и крышка, вал и шестерёнка. Без неё
                 * два контура рядом читаются как одна деталь с уступом.
                 */
                if (s.type === 'label') {
                    return <text key={i} className={cl.partLabel}
                        x={S(s.x)} y={S(s.y)} textAnchor="middle">{s.text}</text>;
                }
                /*
                 * ВЫНОСКА НА ПОЛКЕ — для размеров, которым не хватает места
                 * рядом с деталью: толщина стенки, мелкая фаска, узкий паз.
                 *
                 * По ГОСТ 2.316: наклонная линия от элемента, горизонтальная
                 * полка, надпись НАД полкой. Так подписывают то, что физически
                 * не вмещает обычную размерную цепочку.
                 *
                 * { type:'lead', x, y, tx, ty, text } — от точки (x,y) детали
                 * к полке, начинающейся в (tx,ty).
                 */
                if (s.type === 'lead') {
                    const shelf = S(s.tx) + (s.tx >= s.x ? 26 : -26);
                    return (
                        <g key={i}>
                            <line className={cl.leadLine}
                                x1={S(s.x)} y1={S(s.y)}
                                x2={S(s.tx)} y2={S(s.ty)}/>
                            <line className={cl.leadLine}
                                x1={S(s.tx)} y1={S(s.ty)}
                                x2={shelf} y2={S(s.ty)}/>
                            <circle className={cl.leadDot}
                                cx={S(s.x)} cy={S(s.y)} r={1.2}/>
                            <text className={cl.dimText}
                                x={(S(s.tx) + shelf) / 2} y={S(s.ty) - 3}
                                textAnchor="middle">{s.text}</text>
                        </g>
                    );
                }
                return null;
            })}

            {dims.map((d, i) => (
                <Dimension key={i} {...d}
                    x1={S(d.x1)} y1={S(d.y1)} x2={S(d.x2)} y2={S(d.y2)}
                    from={d.from === undefined ? undefined : S(d.from)}/>
            ))}
        </g>
    );
}


/**
 * Чертёж детали в трёх проекциях.
 *
 * @param {Object} blueprint — описание из урока:
 *   { front:{ w, h, shapes, dims }, side:{...}, top:{...}, note? }
 *   Размеры видов задаются в миллиметрах детали — масштаб подбирается сам.
 */
const Blueprint = ({ blueprint }) => {
    if (!blueprint) return null;
    const { front, side, top, note } = blueprint;

    /*
     * Масштаб — один на весь чертёж, чтобы виды были СОРАЗМЕРНЫ друг другу:
     * деталь 60 мм на виде сверху должна выглядеть ровно так же, как те же
     * 60 мм на главном виде. Считаем по суммарной ширине раскладки.
     */
    const mmW = (front?.w || 0) + GAP / 2 + (side?.w || 0);
    const mmH = (front?.h || 0) + GAP / 2 + (top?.h || 0);
    const k = (CANVAS - PAD * 2 - DIM_OUT * 3 - GAP) / Math.max(mmW, 1);

    const S = (v) => v * k;

    /*
     * Насколько содержимое видов вылезает ВЛЕВО за нулевую координату:
     * вертикальные размеры стоят на отрицательных x, выноски уходят ещё
     * дальше. Без этого запаса левый край чертежа обрезается.
     */
    const leftBleed = Math.max(0, ...[front, side, top].filter(Boolean).flatMap((v) => [
        ...(v.dims || []).map((d) => -Math.min(d.x1, d.x2, d.from ?? Infinity)),
        ...(v.shapes || []).filter((sh) => sh.type === 'lead')
            .map((sh) => -Math.min(sh.x, sh.tx) + 30 / k),
    ])) * k;

    // Раскладка по первому углу: вид слева — справа от главного,
    // вид сверху — под главным.
    const frontX = PAD + Math.max(DIM_OUT * 1.8, leftBleed + 6);
    const frontY = PAD + DIM_OUT + 14;
    const sideX = frontX + S(front?.w || 0) + GAP + DIM_OUT;
    const sideY = frontY;
    const topX = frontX;
    const topY = frontY + S(front?.h || 0) + GAP + DIM_OUT + 30;

    const totalW = Math.max(
        sideX + S(side?.w || 0) + DIM_OUT * 1.6 + PAD,
        topX + S(top?.w || 0) + DIM_OUT * 1.6 + PAD,
    );
    const totalH = topY + S(top?.h || 0) + DIM_OUT * 1.8 + PAD;

    return (
        <figure className={cl.wrap}>
            <svg className={cl.svg}
                viewBox={`0 0 ${totalW} ${totalH}`}
                role="img"
                aria-label="Чертёж детали в трёх проекциях">
                {front && <View title="Спереди" {...front}
                    offsetX={frontX} offsetY={frontY} k={k}/>}
                {side && <View title="Слева" {...side}
                    offsetX={sideX} offsetY={sideY} k={k}/>}
                {top && <View title="Сверху" {...top}
                    offsetX={topX} offsetY={topY} k={k}/>}
            </svg>
            <figcaption className={cl.caption}>
                {note || 'Все размеры в миллиметрах. Штриховая линия — то, '
                    + 'что скрыто внутри детали.'}
            </figcaption>
        </figure>
    );
};

export default Blueprint;
